import React, {useEffect, useMemo, useState} from 'react';
import {
    Alert,
    Chip,
    CircularProgress,
    Link,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import {useNavigate} from 'react-router';
import {getEeiAnalyticCoverage} from '../../../firebase/firestore/eei';
import {getUsersByIds} from '../../../firebase/firestore/users';
import AuthorChip from '../../products/components/common/AuthorChip';
import {indexUsersById, toAvatarProfile, userDisplayName} from '../../collection/components/PersonCell';
import CoverageBar from '../coverage/components/CoverageBar';

function fitChip(fintel) {
    if (fintel.fit === 'full') {
        return <Chip size="small" color="success" label="Fully Answers" sx={{height: 22}}/>;
    }
    const pct = Math.round(fintel.answerPct ?? 0);
    return <Chip size="small" color="warning" label={`Partially Answers (${pct}%)`} sx={{height: 22}}/>;
}

function CoverageNarratives({coveredSummary, uncoveredSummary, explanationFallback = null}) {
    const covered = String(coveredSummary || '').trim();
    const uncovered = String(uncoveredSummary || '').trim();
    if (!covered && !uncovered) {
        if (!explanationFallback) return null;
        return (
            <Typography variant="body2" color="text.secondary">
                {explanationFallback}
            </Typography>
        );
    }
    return (
        <Stack spacing={0.5}>
            {covered && (
                <Typography variant="body2">
                    {covered}
                </Typography>
            )}
            {uncovered && (
                <Typography variant="body2" color="text.secondary">
                    {uncovered}
                </Typography>
            )}
        </Stack>
    );
}

/**
 * FINTEL tab of the EEI page: analytic coverage of this EEI's question (from
 * contract C1 via /api/eeis/{id}/analytic-coverage) and every FINTEL with a
 * nonzero contribution. AR weights are never rendered.
 */
export default function EeiFintelTab({eeiId}) {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [authorsById, setAuthorsById] = useState({});

    useEffect(() => {
        if (!eeiId) {
            setData(null);
            setLoading(false);
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        getEeiAnalyticCoverage(eeiId)
            .then((result) => {
                if (cancelled) return;
                setData(result);
                setError(null);
            })
            .catch((e) => {
                if (cancelled) return;
                setError(e?.message || 'Failed to load analytic coverage');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [eeiId]);

    const authorIdsKey = useMemo(() => {
        const fintels = Array.isArray(data?.fintels) ? data.fintels : [];
        return [...new Set(fintels.map((f) => f.authorUserId).filter(Boolean))].sort().join(',');
    }, [data]);

    // AuthorChip defers expert fetch until the dialog opens, so resolve display
    // names up front for the inline label (coverage API only returns user ids).
    useEffect(() => {
        const authorIds = authorIdsKey ? authorIdsKey.split(',') : [];
        if (authorIds.length === 0) {
            setAuthorsById({});
            return undefined;
        }
        let cancelled = false;
        getUsersByIds(authorIds)
            .then((users) => {
                if (!cancelled) setAuthorsById(indexUsersById(users));
            })
            .catch(() => {
                if (!cancelled) setAuthorsById({});
            });
        return () => {
            cancelled = true;
        };
    }, [authorIdsKey]);

    if (loading) {
        return (
            <Stack alignItems="center" py={4} spacing={1}>
                <CircularProgress size={28}/>
                <Typography variant="body2" color="text.secondary">Loading analytic coverage…</Typography>
            </Stack>
        );
    }

    if (error) {
        return <Alert severity="error" variant="outlined">{error}</Alert>;
    }

    const coveragePct = data?.coveragePct;
    const fintels = Array.isArray(data?.fintels) ? data.fintels : [];

    if (coveragePct == null && fintels.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" py={1}>
                No analytic coverage yet for this EEI
            </Typography>
        );
    }

    return (
        <Stack spacing={2.5}>
            <Stack spacing={1}>
                <CoverageBar
                    value={coveragePct}
                    label="Analytic"
                    height={5}
                    showValue
                />
                <CoverageNarratives
                    coveredSummary={data?.coveredSummary}
                    uncoveredSummary={data?.uncoveredSummary}
                    explanationFallback={data?.explanation}
                />
            </Stack>

            {fintels.length === 0 ? (
                <Typography variant="body2" color="text.secondary" py={1}>
                    No FINTEL reports contribute to this EEI yet
                </Typography>
            ) : (
                <Stack spacing={1.5}>
                    {fintels.map((fintel) => {
                        const title = fintel.title || 'Untitled FINTEL';
                        const authorProfile = authorsById[fintel.authorUserId] || null;
                        return (
                            <Paper key={fintel.reportId} variant="outlined" sx={{p: 1.5, borderRadius: 1}}>
                                <Stack spacing={0.75}>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                        {fitChip(fintel)}
                                        {fintel.reportId ? (
                                            <Link
                                                component="button"
                                                type="button"
                                                variant="subtitle2"
                                                underline="hover"
                                                onClick={() => navigate(
                                                    `/products/${encodeURIComponent(fintel.reportId)}/preview`,
                                                )}
                                                sx={{
                                                    flex: 1,
                                                    minWidth: 0,
                                                    fontWeight: 700,
                                                    textAlign: 'left',
                                                }}
                                                title={title}
                                            >
                                                <Typography
                                                    component="span"
                                                    variant="subtitle2"
                                                    fontWeight={700}
                                                    noWrap
                                                    sx={{display: 'block'}}
                                                >
                                                    {title}
                                                </Typography>
                                            </Link>
                                        ) : (
                                            <Typography
                                                variant="subtitle2"
                                                fontWeight={700}
                                                sx={{flex: 1, minWidth: 0}}
                                                noWrap
                                                title={title}
                                            >
                                                {title}
                                            </Typography>
                                        )}
                                        {fintel.authorUserId && (
                                            <AuthorChip
                                                userId={fintel.authorUserId}
                                                userDisplayName={
                                                    userDisplayName(authorProfile) || fintel.authorUserId
                                                }
                                                flex={undefined}
                                                avatarProfile={toAvatarProfile(authorProfile)}
                                            />
                                        )}
                                    </Stack>
                                    <CoverageNarratives
                                        coveredSummary={fintel.coveredSummary}
                                        uncoveredSummary={fintel.uncoveredSummary}
                                    />
                                </Stack>
                            </Paper>
                        );
                    })}
                </Stack>
            )}
        </Stack>
    );
}
